/**
 * Stream “Prospects” table: infinite scroll, filters (step/status/search), row actions, and pipeline context.
 *
 * Where: primary prospects tab on `StreamDetails`.
 * Uses: pipeline/prospect APIs via React Query (see file imports); responsive table vs card patterns.
 * Behavior: debounced search, multi-select filters, kebab menus for prospect actions.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Skeleton,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Sort as SortIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
} from '@mui/icons-material';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { streamApi, type ProspectPipeline } from '@/api/stream';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';

interface ProspectPipelineListProps {
  streamId: string;
}

const formatAvailableAt = (value?: string | null): string => {
  if (!value) {
    return 'N/A';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'N/A';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const PROSPECT_PIPELINE_STATUSES = [
  'Todo',
  'In progress',
  'Done',
  'Failed',
  'Pending',
  'Stop',
  'Preparing',
  'Moved Queue',
  'Airtable Todo',
] as const;

type ProspectPipelineStatus = (typeof PROSPECT_PIPELINE_STATUSES)[number];

interface ProspectMenuProps {
  prospect: ProspectPipeline;
  onRemove: (pipelineId: string) => void;
  onStop: (pipelineId: string) => void;
  onRetry: (pipelineId: string) => void;
  onRunNow: (pipelineId: string) => void;
}

const ProspectMenu: React.FC<ProspectMenuProps> = ({
  prospect,
  onRemove,
  onStop,
  onRetry,
  onRunNow,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRemove = () => {
    onRemove(prospect.key);
    handleClose();
  };

  const handleStop = () => {
    onStop(prospect.key);
    handleClose();
  };

  const handleRetry = () => {
    onRetry(prospect.key);
    handleClose();
  };

  const handleRunNow = () => {
    onRunNow(prospect.key);
    handleClose();
  };

  const canRetry = prospect.status === 'Failed';
  const canRunNow =
    prospect.status === 'Todo' ||
    prospect.status === 'Failed' ||
    prospect.status === 'Stop';
  const isStopped = prospect.status === 'Stop';

  return (
    <>
      <IconButton onClick={handleClick} size="small">
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={handleRemove}>Remove from pipeline</MenuItem>
        <MenuItem onClick={handleStop} disabled={isStopped}>
          Stop
        </MenuItem>
        <MenuItem onClick={handleRetry} disabled={!canRetry}>
          Retry
        </MenuItem>
        <MenuItem onClick={handleRunNow} disabled={!canRunNow}>
          Run now
        </MenuItem>
      </Menu>
    </>
  );
};

const ProspectCard: React.FC<{
  prospect: ProspectPipeline;
  onRemove: (pipelineId: string) => void;
  onStop: (pipelineId: string) => void;
  onRetry: (pipelineId: string) => void;
  onRunNow: (pipelineId: string) => void;
}> = ({
  prospect,
  onRemove,
  onStop,
  onRetry,
  onRunNow,
}) => {
  const navigate = useNavigate();
  const stepCategories = prospect.step_categories || [];

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            src={
              typeof prospect.prospect.profile_image === 'string' 
                ? prospect.prospect.profile_image 
                : prospect.prospect.profile_image?.url
            }
            sx={{ width: 48, height: 48 }}
            onClick={() => navigate(`/leads/${prospect.prospect.key}`)}
            style={{ cursor: 'pointer' }}
          >
            <PersonIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="subtitle1" 
              noWrap 
              sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
              onClick={() => navigate(`/leads/${prospect.prospect.key}`)}
            >
              {prospect.prospect.full_name || `${prospect.prospect.first_name || ''} ${prospect.prospect.last_name || ''}`.trim() || 'Unknown'}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {prospect.prospect.title || 'No title'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {prospect.prospect.location || 'No location'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {stepCategories.length > 0 ? (
              stepCategories.map((category, index) => (
                <Chip key={index} label={category} size="small" variant="outlined" />
              ))
            ) : (
              <Chip label="Unknown" size="small" variant="outlined" />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={prospect.status || 'Unknown'}
              size="small"
              color={prospect.status === 'Done' ? 'success' : prospect.status === 'Failed' ? 'error' : 'default'}
              variant="outlined"
            />
            <ProspectMenu
              prospect={prospect}
              onRemove={onRemove}
              onStop={onStop}
              onRetry={onRetry}
              onRunNow={onRunNow}
            />
          </Box>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1.5, display: 'block', whiteSpace: 'pre-line' }}
        >
          {prospect.activity_summary || '-'}
        </Typography>
      </CardContent>
    </Card>
  );
};

const SkeletonRows: React.FC<{ count: number }> = ({ count }) => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      <TableRow key={index}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box>
              <Skeleton variant="text" width={120} />
              <Skeleton variant="text" width={80} />
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={100} />
        </TableCell>
        <TableCell>
          <Skeleton variant="rectangular" width={80} height={24} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={80} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={120} />
        </TableCell>
        <TableCell>
          <Skeleton variant="rectangular" width={80} height={24} />
        </TableCell>
        <TableCell>
          <Skeleton variant="circular" width={24} height={24} />
        </TableCell>
      </TableRow>
    ))}
  </>
);

export const ProspectPipelineList: React.FC<ProspectPipelineListProps> = ({ streamId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const isInternalUpdateRef = useRef(false);

  const parseStepCategoriesParam = (params: URLSearchParams): string[] => {
    const listValues = params.getAll('step_categories').filter(Boolean);
    if (listValues.length > 0) {
      if (listValues.length === 1 && listValues[0].includes(',')) {
        return listValues[0].split(',').map((value) => value.trim()).filter(Boolean);
      }
      return listValues;
    }
    const rawValue = params.get('step_categories');
    if (!rawValue) {
      return [];
    }
    return rawValue.split(',').map((value) => value.trim()).filter(Boolean);
  };

  const getSortFieldFromParams = (params: URLSearchParams): string => {
    const value = params.get('sort_field');
    const validValues = streamApi.getStreamLeadsSortOptions().map((o) => o.value);
    return value && validValues.includes(value) ? value : 'available_at';
  };

  const getSortDirectionFromParams = (params: URLSearchParams): 'asc' | 'desc' => {
    return params.get('sort_direction') === 'desc' ? 'desc' : 'asc';
  };

  const [stepCategories, setStepCategories] = useState<string[]>(() =>
    parseStepCategoriesParam(searchParams)
  );
  const [status, setStatus] = useState<ProspectPipelineStatus | ''>(() => {
    const value = searchParams.get('status');
    return value && PROSPECT_PIPELINE_STATUSES.includes(value as ProspectPipelineStatus)
      ? (value as ProspectPipelineStatus)
      : '';
  });
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [sortField, setSortField] = useState<string>(() =>
    getSortFieldFromParams(searchParams)
  );
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() =>
    getSortDirectionFromParams(searchParams)
  );
  const [activity, setActivity] = useState<string>(() => searchParams.get('activity') || '');
  const [happenedAfter, setHappenedAfter] = useState<string>(() => searchParams.get('happened_after') || '');
  const [happenedBefore, setHappenedBefore] = useState<string>(() => searchParams.get('happened_before') || '');
  const debouncedSearch = useDebouncedValue(search, 500);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const updateURLParams = useCallback(
    (updates: {
      search?: string;
      status?: ProspectPipelineStatus | '';
      stepCategories?: string[];
      sortField?: string;
      sortDirection?: 'asc' | 'desc';
      activity?: string;
      happenedAfter?: string;
      happenedBefore?: string;
    }) => {
      isInternalUpdateRef.current = true;
      setSearchParams((currentSearchParams) => {
        const newSearchParams = new URLSearchParams(currentSearchParams);

        if (updates.search !== undefined) {
          if (updates.search) {
            newSearchParams.set('search', updates.search);
          } else {
            newSearchParams.delete('search');
          }
        }

        if (updates.status !== undefined) {
          if (updates.status) {
            newSearchParams.set('status', updates.status);
          } else {
            newSearchParams.delete('status');
          }
        }

        if (updates.stepCategories !== undefined) {
          if (updates.stepCategories.length > 0) {
            newSearchParams.set('step_categories', updates.stepCategories.join(','));
          } else {
            newSearchParams.delete('step_categories');
          }
        }

        if (updates.sortField !== undefined) {
          newSearchParams.set('sort_field', updates.sortField);
        }

        if (updates.sortDirection !== undefined) {
          newSearchParams.set('sort_direction', updates.sortDirection);
        }

        if (updates.activity !== undefined) {
          if (updates.activity) {
            newSearchParams.set('activity', updates.activity);
          } else {
            newSearchParams.delete('activity');
          }
        }

        if (updates.happenedAfter !== undefined) {
          if (updates.happenedAfter) {
            newSearchParams.set('happened_after', updates.happenedAfter);
          } else {
            newSearchParams.delete('happened_after');
          }
        }

        if (updates.happenedBefore !== undefined) {
          if (updates.happenedBefore) {
            newSearchParams.set('happened_before', updates.happenedBefore);
          } else {
            newSearchParams.delete('happened_before');
          }
        }

        // This component owns filters for the default Leads tab only.
        newSearchParams.delete('tab');

        return newSearchParams;
      }, { replace: true });
      setTimeout(() => {
        isInternalUpdateRef.current = false;
      }, 0);
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (isInternalUpdateRef.current) {
      return;
    }

    const urlSearch = searchParams.get('search') || '';
    const urlStatus = searchParams.get('status');
    const urlStepCategories = parseStepCategoriesParam(searchParams);
    const urlSortField = getSortFieldFromParams(searchParams);
    const urlSortDirection = getSortDirectionFromParams(searchParams);
    const urlActivity = searchParams.get('activity') || '';
    const urlHappenedAfter = searchParams.get('happened_after') || '';
    const urlHappenedBefore = searchParams.get('happened_before') || '';

    if (urlSearch !== search) {
      setSearch(urlSearch);
    }

    const nextStatus =
      urlStatus && PROSPECT_PIPELINE_STATUSES.includes(urlStatus as ProspectPipelineStatus)
        ? (urlStatus as ProspectPipelineStatus)
        : '';
    if (nextStatus !== status) {
      setStatus(nextStatus);
    }

    if (urlStepCategories.join(',') !== stepCategories.join(',')) {
      setStepCategories(urlStepCategories);
    }

    if (urlSortField !== sortField) {
      setSortField(urlSortField);
    }

    if (urlSortDirection !== sortDirection) {
      setSortDirection(urlSortDirection);
    }
    if (urlActivity !== activity) {
      setActivity(urlActivity);
    }
    if (urlHappenedAfter !== happenedAfter) {
      setHappenedAfter(urlHappenedAfter);
    }
    if (urlHappenedBefore !== happenedBefore) {
      setHappenedBefore(urlHappenedBefore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    updateURLParams({
      search: debouncedSearch,
      status,
      stepCategories,
      sortField,
      sortDirection,
      activity,
      happenedAfter,
      happenedBefore,
    });
  }, [debouncedSearch, status, stepCategories, sortField, sortDirection, activity, happenedAfter, happenedBefore, updateURLParams]);

  // Infinite query for prospect pipelines
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: [
      'prospect-pipelines',
      streamId,
      stepCategories,
      debouncedSearch,
      status,
      sortField,
      sortDirection,
      activity,
      happenedAfter,
      happenedBefore,
    ],
    queryFn: ({ pageParam }) =>
      streamApi.getProspectPipelines(streamId, {
        page_token: pageParam,
        max_results: 50,
        step_categories: stepCategories.length > 0 ? stepCategories : undefined,
        search: debouncedSearch || undefined,
        status: status || undefined,
        sort_field: sortField,
        sort_direction: sortDirection,
        activity: activity || undefined,
        happened_after: happenedAfter || undefined,
        happened_before: happenedBefore || undefined,
      }),
    getNextPageParam: (lastPage) => lastPage.output.next_page_token,
    initialPageParam: undefined as string | undefined,
  });

  // Remove prospect mutation
  const removeProspectMutation = useMutation({
    mutationFn: (prospectPipelineId: string) =>
      streamApi.removeProspectPipeline(prospectPipelineId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['prospect-pipelines', streamId],
      });
      setActionError(null);
      setActionMessage('Prospect removed from pipeline');
    },
    onError: (error: any) => {
      console.error('Failed to remove prospect:', error);
      setActionMessage(null);
      setActionError('Failed to remove prospect from pipeline');
    },
  });

  const stopProspectMutation = useMutation({
    mutationFn: (prospectPipelineId: string) =>
      streamApi.stopProspectPipeline(prospectPipelineId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['prospect-pipelines', streamId],
      });
      setActionError(null);
      setActionMessage('Prospect pipeline stopped');
    },
    onError: (error: any) => {
      console.error('Failed to stop prospect pipeline:', error);
      setActionMessage(null);
      setActionError('Failed to stop prospect pipeline');
    },
  });

  const retryProspectMutation = useMutation({
    mutationFn: (prospectPipelineId: string) =>
      streamApi.retryProspectPipeline(prospectPipelineId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['prospect-pipelines', streamId],
      });
      setActionError(null);
      setActionMessage('Prospect pipeline retried');
    },
    onError: (error: any) => {
      console.error('Failed to retry prospect pipeline:', error);
      setActionMessage(null);
      setActionError('Failed to retry prospect pipeline');
    },
  });

  const runNowProspectMutation = useMutation({
    mutationFn: (prospectPipelineId: string) =>
      streamApi.runProspectPipelineNow(prospectPipelineId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['prospect-pipelines', streamId],
      });
      setActionError(null);
      setActionMessage('Prospect pipeline queued to run now');
    },
    onError: (error: any) => {
      console.error('Failed to run prospect pipeline now:', error);
      setActionMessage(null);
      setActionError('Failed to run prospect pipeline now');
    },
  });

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const sentinel = sentinelRef.current;
    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  const handleRemoveProspect = useCallback(
    (prospectPipelineId: string) => {
      if (window.confirm('Are you sure you want to remove this prospect from the pipeline?')) {
        removeProspectMutation.mutate(prospectPipelineId);
      }
    },
    [removeProspectMutation]
  );

  const handleStopProspect = useCallback(
    (prospectPipelineId: string) => {
      stopProspectMutation.mutate(prospectPipelineId);
    },
    [stopProspectMutation],
  );

  const handleRetryProspect = useCallback(
    (prospectPipelineId: string) => {
      retryProspectMutation.mutate(prospectPipelineId);
    },
    [retryProspectMutation],
  );

  const handleRunNowProspect = useCallback(
    (prospectPipelineId: string) => {
      runNowProspectMutation.mutate(prospectPipelineId);
    },
    [runNowProspectMutation],
  );

  const prospects = data?.pages.flatMap((page) => page.output.items) || [];
  const availableStepCategories = streamApi.getStepCategories();
  const availableActivities = streamApi.getActivityFilters();
  const sortOptions = streamApi.getStreamLeadsSortOptions();

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load prospects. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      {actionMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionMessage(null)}>
          {actionMessage}
        </Alert>
      )}
      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: 'wrap',
        }}
      >
        <TextField
          placeholder="Search prospects by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Step Categories</InputLabel>
          <Select
            multiple
            value={stepCategories}
            onChange={(e) => setStepCategories(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
            input={<OutlinedInput label="Step Categories" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {availableStepCategories.map((category) => (
              <MenuItem key={category} value={category}>
                <Checkbox checked={stepCategories.indexOf(category) > -1} />
                <ListItemText primary={category} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={status}
            label="Status"
            onChange={(e) => setStatus(e.target.value as ProspectPipelineStatus | '')}
          >
            <MenuItem value="">
              <em>All statuses</em>
            </MenuItem>
            {PROSPECT_PIPELINE_STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Action/Event</InputLabel>
          <Select
            value={activity}
            label="Action/Event"
            onChange={(e) => setActivity(String(e.target.value))}
          >
            <MenuItem value="">
              <em>All actions/events</em>
            </MenuItem>
            {availableActivities.map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="datetime-local"
          label="Happened After"
          value={happenedAfter}
          onChange={(e) => setHappenedAfter(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 220 }}
        />
        <TextField
          size="small"
          type="datetime-local"
          label="Happened Before"
          value={happenedBefore}
          onChange={(e) => setHappenedBefore(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 220 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortField}
              label="Sort By"
              onChange={(e) => setSortField(e.target.value)}
              startAdornment={<SortIcon fontSize="small" sx={{ mr: 1 }} />}
            >
              {sortOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title={`Sort ${sortDirection === 'desc' ? 'Ascending' : 'Descending'}`}>
            <IconButton
              size="small"
              onClick={() => setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc')}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                p: 1,
              }}
            >
              {sortDirection === 'desc' ? (
                <ArrowDownIcon fontSize="small" />
              ) : (
                <ArrowUpIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Mobile View - Cards */}
      {isMobile ? (
        <Box>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <Card key={index} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Skeleton variant="circular" width={48} height={48} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="text" width="40%" />
                      <Skeleton variant="text" width="30%" />
                    </Box>
                  </Box>
                  <Skeleton variant="rectangular" width={80} height={24} />
                </CardContent>
              </Card>
            ))
          ) : prospects.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography color="text.secondary">No prospects found</Typography>
            </Box>
          ) : (
            prospects.map((prospect) => (
              <ProspectCard
                key={prospect.key}
                prospect={prospect}
                onRemove={handleRemoveProspect}
                onStop={handleStopProspect}
                onRetry={handleRetryProspect}
                onRunNow={handleRunNowProspect}
              />
            ))
          )}
        </Box>
      ) : (
        /* Desktop View - Table */
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Prospect</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Step Category</TableCell>
                <TableCell>Activities</TableCell>
                <TableCell>Available At</TableCell>
                <TableCell width={50}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <SkeletonRows count={10} />
              ) : prospects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography color="text.secondary">No prospects found</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                prospects.map((prospect) => {
                  const rowStepCategories = prospect.step_categories || [];
                  return (
                    <TableRow key={prospect.key} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={
                              typeof prospect.prospect.profile_image === 'string'
                                ? prospect.prospect.profile_image
                                : prospect.prospect.profile_image?.url
                            }
                            sx={{ width: 40, height: 40 }}
                            onClick={() => navigate(`/leads/${prospect.prospect.key}`)}
                            style={{ cursor: 'pointer' }}
                          >
                            <PersonIcon />
                          </Avatar>
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                cursor: 'pointer',
                                '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                              }}
                              onClick={() => navigate(`/leads/${prospect.prospect.key}`)}
                            >
                              {prospect.prospect.full_name ||
                                `${prospect.prospect.first_name || ''} ${
                                  prospect.prospect.last_name || ''
                                }`.trim() ||
                                'Unknown'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {prospect.prospect.title || 'No title'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {prospect.prospect.location || 'No location'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={prospect.status || 'Unknown'}
                          size="small"
                          color={
                            prospect.status === 'Done'
                              ? 'success'
                              : prospect.status === 'Failed'
                              ? 'error'
                              : 'default'
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {rowStepCategories.length > 0 ? (
                            rowStepCategories.map((category, index) => (
                              <Chip key={index} label={category} size="small" variant="outlined" />
                            ))
                          ) : (
                            <Chip label="Unknown" size="small" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', whiteSpace: 'pre-line' }}
                        >
                          {prospect.activity_summary || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatAvailableAt(prospect.available_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <ProspectMenu
                          prospect={prospect}
                          onRemove={handleRemoveProspect}
                          onStop={handleStopProspect}
                          onRetry={handleRetryProspect}
                          onRunNow={handleRunNowProspect}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} style={{ height: 20 }}>
        {isFetchingNextPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={32} />
          </Box>
        )}
      </div>
    </Box>
  );
};
